import { Alert, Box, Button, DialogActions, DialogContent, DialogContentText, Divider, Stack } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { TableInstance } from 'react-table';
import { useUpdateQuote } from 'shared/mutations';
import { Product } from 'shared/types';
import { calculateMargin, compareToMarginFloor } from 'shared/utility';
import NumberTextField from './number-text-field';
import OverrideUntilField from './override-until-field';
import RevisionSelectField from './revision-select-field';

interface EditPageProps {
    instance: TableInstance<Product>;
    handleClose: () => void;
}

export default function EditPage({ instance, handleClose }: EditPageProps) {
    const { selectedFlatRows } = instance;
    const row = useMemo(() => selectedFlatRows?.[0]?.original || { productCode: '' }, [selectedFlatRows]);
    const methods = useForm({
        defaultValues: row,
    });

    const {
        handleSubmit,
        watch,
        setValue,
        formState: { errors, isDirty },
    } = methods;
    const id = row.productCode;
    const revisedPrice = watch('revisedPrice');

    const { mutateAsync, isLoading } = useUpdateQuote();

    const productMargin = calculateMargin(revisedPrice, row.productCostUnit);

    const onSubmit = useCallback(
        async data => {
            await mutateAsync({
                id,
                data,
            });

            handleClose();
        },
        [id, mutateAsync, handleClose]
    );

    const quantityBreaks = useMemo(
        () => [
            { name: 'quantityBreak2', label: 'Quantity 50' },
            { name: 'quantityBreak3', label: 'Quantity 100' },
            { name: 'quantityBreak4', label: 'Quantity 150' },
            { name: 'quantityBreak5', label: 'Quantity 200' },
        ],
        []
    );

    const showWarning = compareToMarginFloor(productMargin, row.marginFloor);

    const quantityBreaksUsed = row.quantityBreaksUsed === 'Y';
    const resetToCurrentPrice = useCallback(() => {
        setValue('revisedPrice', row.currentPrice);
    }, [row, setValue]);
    const resetToRecommendedPrice = useCallback(() => {
        setValue('revisedPrice', row.recommendedPrice);
    }, [row, setValue]);

    return (
        <DialogContent>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box sx={quantityBreaksUsed ? { display: 'grid', gridTemplateColumns: '49% 1% 49%' } : undefined}>
                        <Box padding={1.25} minHeight='300px'>
                            <DialogContentText>Revise</DialogContentText>
                            <div>
                                Current Price:{' '}
                                <Button color='secondary' onClick={resetToCurrentPrice}>
                                    <strong>${Number(row.currentPrice).toFixed(2)}</strong>
                                </Button>
                            </div>
                            <div>
                                Recommended Price:{' '}
                                <Button color='secondary' onClick={resetToRecommendedPrice}>
                                    <strong>${Number(row.recommendedPrice).toFixed(2)}</strong>
                                </Button>
                            </div>
                            <p>
                                New Product Margin:{' '}
                                <strong>{Number.isFinite(productMargin) ? productMargin.toFixed(1) : 0}</strong>%
                            </p>
                            <Stack spacing={1}>
                                <NumberTextField prefix='$ ' name='revisedPrice' label='Revised Price' />
                                <RevisionSelectField
                                    name='revisionReason'
                                    label='Select a reason'
                                    rules={{
                                        required: { value: true, message: 'Reason is required' },
                                    }}
                                    error={Boolean(errors.revisionReason)}
                                    helperText={Boolean(errors.revisionReason) ? errors.revisionReason?.message : ''}
                                />
                                <OverrideUntilField name='overrideUntil' label='Override Until' />
                            </Stack>
                            {showWarning ? (
                                <Alert sx={{ mt: 1 }} severity='warning'>
                                    <strong>Price is below Margin Floor</strong>
                                </Alert>
                            ) : null}
                        </Box>
                        {quantityBreaksUsed && (
                            <>
                                <Divider sx={{ width: 2 }} orientation='vertical' />
                                <Box pt={1} pl={1}>
                                    <DialogContentText sx={{ pb: 1 }}>Quantity Breaks</DialogContentText>
                                    <Stack spacing={1}>
                                        {quantityBreaks.map(({ name, label }) => (
                                            <NumberTextField key={name} prefix='$ ' name={name} label={label} />
                                        ))}
                                    </Stack>
                                </Box>
                            </>
                        )}
                    </Box>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button disabled={!isDirty} type='submit' variant='contained' color='primary'>
                            {isLoading ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </form>
            </FormProvider>
        </DialogContent>
    );
}
